import { FluidKeyedObject } from '@lmig/fluid-core';

import { GLOBAL_HTML_ATTRS } from '../../constants/constants';
import { Utils } from '../utils';

// ================================================================ //
// -- HTML Attribute Tunneling

/**
 * Finds all native HTML attributes on a component's host element
 * (i.e. "this.host") that are included in the "supportedAttrs"
 * parameter, then returns them as key/value pairs in an "attribute
 * map" object. The resulting attribute map can then be applied to
 * an appropriate element within the component's Shadow DOM
 * (i.e. "tunneling" the attributes into the Shadow DOM).
 * @param hostRef - the component's "host" element (NOT the component class itself)
 * @param supportedAttrs - array of attribute names that are supported for "tunneling"
 */
export const getNativeAttrMap = (
  hostRef: HTMLElement,
  supportedAttrs: string[] = GLOBAL_HTML_ATTRS
) => {
  if (!hostRef || typeof hostRef.getAttributeNames !== 'function') return;

  return hostRef
    .getAttributeNames()
    .reduce((attrList: FluidKeyedObject<string>, attr: string) => {
      return !supportedAttrs.includes(attr)
        ? attrList
        : {
            ...attrList,
            [attr]: hostRef.getAttribute(attr),
          };
    }, {} as FluidKeyedObject<string>);
};

/**
 * Updates a component's "_attrMap" State with supported attribute
 * keys/values from the host element (i.e. "this.host"), then removes
 * the attributes from the host element. Attributes are removed from the
 * host element to prevent accessibility issues caused by duplicated
 * attrs (e.g. if a "title" attribute existed on both the host element
 * AND an internal element, screen readers would announce it twice).
 * @param host - the target component class (NOT the component's "host" element)
 * @param supportedAttrs - array of attribute names that are supported for "tunneling"
 * @requires {@State} host._attrMap - key/value pairs of "tunneled" HTML attrs
 */
export const setHtmlAttrs = (
  host: any,
  supportedAttrs: string[] = GLOBAL_HTML_ATTRS
): void => {
  const { host: hostRef } = host;

  host._attrMap = {
    ...host._attrMap,
    ...getNativeAttrMap(hostRef, supportedAttrs),
  };

  for (const attr in host._attrMap) {
    hostRef.removeAttribute(attr);
  }
};

/**
 * Calls initial "setHtmlAttrs()", then initializes MutationObserver that
 * triggers additional "setHtmlAttrs()" calls when supported attrs change
 * on the "host" element.
 * @param host - the target component class (NOT the component's "host" element)
 * @param supportedAttrs - array of attribute names that are supported for "tunneling"
 * @requires {@State} host._attrMap - key/value pairs of "tunneled" HTML attrs
 */
export const initializeAttrTunnel = (
  host: any,
  supportedAttrs: string[] = GLOBAL_HTML_ATTRS
): void => {
  const { host: hostRef } = host;

  setHtmlAttrs(host, supportedAttrs);
  Utils.watchAttr(supportedAttrs, hostRef, () =>
    setHtmlAttrs(host, supportedAttrs)
  );
};
